import { useImperativeHandle } from 'react';

import { Grid, Dialog, Typography } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { ModalHeader } from 'pages/variants/common/modal-header';

import { SortableOptionsTable } from './components/sortable-options-table';
import { ModalFooter } from './components/modal-footer';
import { VariantLibraryNameInput } from './components/variant-library-name-input';
import { VariantLibrarySelect } from './components/variant-library-select';

import { variantLibrarySchema } from './variant-library.validator';
import { IVariantLibraryModal, TVariantLibraryFormOption, VariantLibrayForm } from './types';
import { styles } from './styles';

export const VariantLibraryModal = ({
  imperativeRef,
  open,
  heading,
  subheading,
  defaultValues,
  isLoading,
  variants,
  onImportChoices,
  onSubmit,
  onClose,
}: IVariantLibraryModal) => {
  const isProjectSelection = !!onImportChoices && !!variants;

  const form = useForm<VariantLibrayForm>({
    defaultValues,
    resolver: yupResolver(variantLibrarySchema(isProjectSelection)),
    mode: false ? 'all' : 'onChange',
  });

  const isEdit = !!defaultValues.options.length;

  useImperativeHandle(imperativeRef, () => ({
    setOption: (newOptions: TVariantLibraryFormOption[]) => {
      const oldOptions = form.getValues('options') || [];
      form.setValue('options', [...oldOptions, ...newOptions]);
    },
  }));

  return (
    <Dialog open={open} onClose={onClose} sx={styles.modal}>
      <ModalHeader
        heading={heading || `${isEdit ? 'Edit' : 'Add'} Choices`}
        subheading={
          subheading ||
          `${isEdit ? 'Edit' : 'Add'} the different choices available for this specific option group.`
        }
      />

      <Grid sx={styles.container}>
        <FormProvider {...form}>
          <Grid sx={styles.content}>
            {isProjectSelection ? (
              <VariantLibrarySelect options={variants} />
            ) : (
              <VariantLibraryNameInput />
            )}
            {isProjectSelection && (
              <Typography sx={styles.variantChoicesLabel}>Variant Choices</Typography>
            )}
            <SortableOptionsTable onImportChoices={onImportChoices} />
          </Grid>
          <ModalFooter
            isLoading={isLoading}
            onSubmit={form.handleSubmit(onSubmit)}
            onClose={onClose}
          />
        </FormProvider>
      </Grid>
    </Dialog>
  );
};
